// charts
// Graphql
import { useLazyQuery, useQuery } from "@apollo/client";
// Libraries
import { Box, Center, Flex, Progress, Text } from "@chakra-ui/react";
// Cookie
import Cookie from "js-cookie";
import React, { useState } from "react";
// Icons
import { BsCardList, BsChatDots, BsClock } from "react-icons/bs";
// recoil
import { useRecoilValue } from "recoil";
import ScheduledMeetings from "../../../components/Classes/ScheduledMeetings";
import { GET_ANALYTICS, GET_CLASS_ANALYTICS } from "../../../graphql/queries/Analytics";
// helpers
import { formatTime } from "../../../Helpers/HelpfulFunctions";
import { allClasses, currentCardIndex } from "../../../State";
import TextIcon from "../../UI/TextIcon";
import AllClassesAnalytics from "./AllClassesAnalytics";
// Components
import AnalyticsCard from "./AnalyticsCard";

// const BadgeBox = () => <Box width={20} height={20} backgroundColor="gray.200" borderRadius={8} />;

const ClassesStat = ({ classesData, isLoading }) => {
  // const progressPercentage = 0;
  const [showComponent, setShowComponent] = useState(false);

  // recoil
  const currentCardIndexValue = useRecoilValue(currentCardIndex);
  const allClassesValue = useRecoilValue(allClasses);

  // Get current class
  let currentClass = allClassesValue && allClassesValue[currentCardIndexValue] && allClassesValue[currentCardIndexValue].class;

  // Get student id
  const uId = Cookie.get("student_id");

  // Get overall analytics data
  const { data, loading } = useQuery(GET_ANALYTICS, {
    variables: {
      studentId: uId,
    },
    fetchPolicy: "cache-first",
    onError: () => {
      return null;
    },
  });

  // Get class analytics data
  const [getClassAnalytics, { loading: classAnalyticsLoading, data: classAnalyticsData }] = useLazyQuery(GET_CLASS_ANALYTICS, {
    variables: {
      classId: currentClass && currentClass._id,
    },
  });

  const classData = classAnalyticsData ? classAnalyticsData.student_class_analytics.data : "";
  const generalAnalyticsData = data ? data.get_student_analytics.data : "";

  const timeSnapshotName = () => {
    if (generalAnalyticsData && generalAnalyticsData?.lessons?.length > 0) {
      return (
        [...generalAnalyticsData.lessons]
          .filter((item: any) => item !== null && item?.time_spent_on_lesson)
          // .sort((a, b) => b - a)
          .map((item: any) => item?.lesson?.lesson_name)
          .slice(0, 3)
      );
    }
    return [];
  };

  React.useEffect(() => {
    getClassAnalytics();
  }, [currentCardIndexValue]);

  React.useEffect(() => {
    setShowComponent(true);
  }, []);

  if (isLoading) return null;

  const chartDataLabels = [
    {
      value: timeSnapshotName() && timeSnapshotName()[0],
      color: "#afd1ff",
    },
    {
      value: timeSnapshotName() && timeSnapshotName()[1],
      color: "#4895ff",
    },
    {
      value: timeSnapshotName() && timeSnapshotName()[2],
      color: "#005EE9",
    },
  ];

  let graphData = [];

  if (generalAnalyticsData?.lessons?.length > 0) {
    generalAnalyticsData?.lessons?.slice(0, 3)?.forEach((lesson, index) => {
      if (!lesson || !lesson?.time_spent_on_lesson) return;
      graphData.push({
        id: lesson?.lesson_name || `${index + 1}`,
        label: lesson?.lesson_name || `lesson ${index + 1}`,
        value: lesson?.time_spent_on_lesson,
        color: chartDataLabels[index]?.color,
      });
    });
  }

  const analyticsDisplay = (seconds: number) => {
    if (!seconds || seconds === 0 || seconds === null) return "0 sec";

    const { hours, minutes } = formatTime(seconds);

    let hrs = hours > 0 ? `${hours} hr` : "";
    let mins = minutes > 0 ? `${minutes} min` : "";

    if (seconds < 60) return seconds + " sec";

    return `${hrs} ${mins}`;
  };

  if (showComponent && classesData && classesData.length > 0) {
    const tsol = analyticsDisplay(classData && classData.total_time_spent_on_lessons);

    const tsolAverage = analyticsDisplay(classData && classData.total_time_spent_on_lessons / currentClass?.lessons?.length);

    return (
      <Flex
        d="flex"
        flexDir="column"
        py={12}
        // mt={8}
      >
        <Box px={8} pb={6} borderBottomWidth="1px">
          <Text textAlign="center" fontSize="xl" px={{ md: 6 }} fontWeight="700" color="gray.500">
            Scheduled Live Sessions
          </Text>
          <ScheduledMeetings />
        </Box>
        <Box borderBottomWidth="1px">
          {/* Loading Icon */}

          {classAnalyticsLoading && !classAnalyticsData && (
            <Center minH="280px" flexDir="column" padding="6" w="100%" h="100%">
              <Box margin="auto" display="flex" flexDir="column" alignItems="center" justifyContent="center">
                <TextIcon mb={2} color="gray.700" fontSize="24px">
                  <use xlinkHref="../../symbols.svg#animated-logo"></use>
                </TextIcon>

                <Progress height="4px" isIndeterminate width="100px" color="primary.500" />
              </Box>
            </Center>
          )}

          {/* Analytics for active class card */}
          {!classAnalyticsLoading && classAnalyticsData && (
            <>
              <Box minH="280px" px={8} py={10}>
                <Text textAlign="center" fontSize="xl" px={{ md: 6 }} fontWeight="700" color="gray.500">
                  Analytics
                  <br />
                  <Text as="span" color="blue.500">
                    {currentClass && currentClass.class_name}&nbsp;
                  </Text>
                </Text>

                {/* Total time spent on lessons and average time spent on lessons */}
                <Flex mt={8} mb={10} w="100%" justifyContent="space-between">
                  <Flex w="48%" flexDir="column">
                    <AnalyticsCard title={tsol} subTitle="Time Spent on lessons" icon={<BsClock />} />
                  </Flex>

                  {/* Avaerage time spent on lesosns */}
                  <Flex w="48%" flexDir="column">
                    <AnalyticsCard
                      // title={`${analyticsDisplay(
                      //   classData && classData.average_time_spent_on_lessons
                      // )}`}
                      title={tsolAverage}
                      subTitle="Average Time Spent on lessons"
                      icon={<BsClock />}
                    />
                  </Flex>
                </Flex>

                {/* Submitted assessments and comments made */}
                <Flex w="100%" justifyContent="space-between">
                  {/* Submitted assessments */}
                  <Flex w="48%" flexDir="column">
                    <AnalyticsCard
                      title={classData?.total_no_of_submitted_assessments || 0}
                      subTitle="Submitted Assessments"
                      icon={<BsCardList />}
                    />
                  </Flex>

                  {/* Total comments made */}
                  <Flex w="48%" flexDir="column">
                    <AnalyticsCard title={classData?.total_no_of_class_comments || 0} subTitle="Comments made" icon={<BsChatDots />} />
                  </Flex>
                </Flex>
              </Box>
            </>
          )}
        </Box>

        {/* All Classes Data */}
        <Flex pt={10} pb={2} px={6} width="100%">
          <AllClassesAnalytics data={graphData} loading={loading} chartDataLabels={chartDataLabels} />
        </Flex>
      </Flex>
    );
  } else {
    return <></>;
  }
};

export default ClassesStat;
